import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import HeroSlider from '../components/HeroSlider';
import ServiceCard from '../components/ServiceCard';
import SEO from '../components/SEO';
import { useEventTracking } from '../hooks/useAnalytics';

function Home() {
    const [openFAQ, setOpenFAQ] = useState(null);
    const parallaxRef = useRef(null);
    const navigate = useNavigate();

    // Initialize analytics tracking
    const { trackButtonClick, trackServiceInquiry } = useEventTracking();

    const toggleFAQ = (index) => {
        setOpenFAQ(openFAQ === index ? null : index);
        // Track FAQ interactions
        trackButtonClick('faq_toggle', `faq_${index}`);
    };

    const handleServiceInquiry = (serviceName) => {
        trackServiceInquiry(serviceName);
        // Navigate to appropriate page based on service
        if (serviceName === 'Insulation') {
            navigate('/insulation');
        } else if (serviceName === 'Healthy Home Inspection') {
            navigate('/healthy-home');
        } else if (serviceName === 'Garage Doors') {
            navigate('/garage-door');
        } else if (serviceName === 'Free Assessment') {
            navigate('/contact');
        }
    };

    const handleGetQuote = () => {
        trackServiceInquiry('Get Quote');
        navigate('/contact');
    };

    return (
        <>
            <SEO />
            <main>
                <HeroSlider />

                {/* Modern Angular Design Section */}
                <section
                    ref={parallaxRef}
                    className="relative bg-brand-cream text-black text-center py-24 md:py-32 overflow-hidden"
                    aria-labelledby="services-heading"
                >

                    {/* Content Container */}
                    <div className="relative max-w-7xl mx-auto px-4">
                        <div className="flex items-center justify-center min-h-[60vh]">
                            {/* Centered Text Content */}
                            <motion.div
                                className="text-center text-black max-w-6xl"
                                initial={{ opacity: 0, y: 100 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 1, ease: "easeOut" }}
                            >
                                <motion.h1
                                    id="services-heading"
                                    className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-8 text-brand-green"
                                    initial={{ opacity: 0, y: 50 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                                >
                                    Auckland's Insulation<br />
                                    Experts
                                </motion.h1>
                                <motion.p
                                    className="text-lg md:text-xl leading-relaxed max-w-3xl mx-auto mb-24 text-gray-700"
                                    initial={{ opacity: 0, y: 50 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
                                >
                                    At Mega Property Solutions, we provide professional insulation services for new builds, renovations, and retrofit projects across Auckland. From thermal and acoustic insulation to fire-rated systems, our experienced team works with homeowners, builders, and developers to deliver high-quality, code-compliant results — every time.
                                </motion.p>

                                {/* Services Grid */}
                                <motion.div
                                    className="grid grid-cols-1 md:grid-cols-3 gap-6"
                                    initial={{ opacity: 0, y: 50 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
                                    role="list"
                                    aria-label="Our main services"
                                >
                                    <div onClick={() => handleServiceInquiry('Insulation')}>
                                        <ServiceCard
                                            title="Insulation"
                                            description="Professional insulation services for ceilings, walls, and underfloor to keep your home warm and energy-efficient."
                                            linkTo="/insulation"
                                            icon={
                                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                                                </svg>
                                            }
                                            delay={0.7}
                                        />
                                    </div>

                                    <div onClick={() => handleServiceInquiry('Garage Doors')}>
                                        <ServiceCard
                                            title="Garage Doors"
                                            description="Quality garage door installation, repair, and maintenance services for security and convenience."
                                            linkTo="/garage-door"
                                            icon={
                                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                </svg>
                                            }
                                            delay={0.8}
                                        />
                                    </div>

                                    <div onClick={() => handleServiceInquiry('Healthy Home Inspection')}>
                                        <ServiceCard
                                            title="Healthy Home Inspection"
                                            description="Comprehensive home health assessments and safety evaluations to ensure your family's well-being and property compliance."
                                            linkTo="/healthy-home"
                                            icon={
                                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            }
                                            delay={0.9}
                                        />
                                    </div>
                                </motion.div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* Trusted Partners Section */}
                <motion.section
                    className="py-24 md:py-32 bg-brand-green overflow-hidden"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.8 }}
                    aria-labelledby="partners-heading"
                >
                    <div className="max-w-7xl mx-auto px-4">
                        <motion.div
                            className="text-center mb-16"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.6 }}
                        >
                            <h2 id="partners-heading" className="text-4xl md:text-5xl font-bold text-white mb-6">
                                Trusted Industry Partners
                            </h2>
                            <p className="text-lg text-white/90 max-w-2xl mx-auto">
                                With strong partnerships with Placemakers, Mitre10, Bunnings, and ITM, we have fast, reliable access to top-quality products at competitive prices.
                            </p>
                        </motion.div>
                    </div>

                    {/* Partner Logos Grid */}
                    <div className="max-w-7xl mx-auto px-4">
                        <motion.div
                            className="w-full"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            role="list"
                            aria-label="Our trusted industry partners"
                        >
                            <div className="space-y-8">
                                {/* Row 1 */}
                                <div className="flex justify-center">
                                    <div className="inline-flex flex-col sm:flex-row items-stretch gap-6 sm:gap-8">
                                        <motion.div
                                            className="bg-white rounded-xl px-6 py-5 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center h-28 md:h-32 w-full sm:w-72 md:w-80"
                                            role="listitem"
                                            whileHover={{ scale: 1.05, y: -5 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            <img
                                                src={require('../images/partners/placemaker.jpg')}
                                                alt="Placemaker - Trusted Partner"
                                                className="max-h-14 md:max-h-16 w-auto object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                                            />
                                        </motion.div>
                                        <motion.div
                                            className="bg-white rounded-xl px-6 py-5 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center h-28 md:h-32 w-full sm:w-72 md:w-80"
                                            role="listitem"
                                            whileHover={{ scale: 1.05, y: -5 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            <img
                                                src={require('../images/partners/pinkbatts.png')}
                                                alt="Pink Batts - Trusted Partner"
                                                className="max-h-14 md:max-h-16 w-auto object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                                            />
                                        </motion.div>
                                    </div>
                                </div>

                                {/* Row 2 */}
                                <div className="flex justify-center">
                                    <div className="inline-flex flex-col sm:flex-row items-stretch gap-6 sm:gap-8">
                                        <motion.div
                                            className="bg-white rounded-xl px-6 py-5 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center h-28 md:h-32 w-full sm:w-72 md:w-80"
                                            role="listitem"
                                            whileHover={{ scale: 1.05, y: -5 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            <img
                                                src={require('../images/partners/bunnings.jpg')}
                                                alt="Bunnings - Trusted Partner"
                                                className="max-h-14 md:max-h-16 w-auto object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                                            />
                                        </motion.div>
                                        <motion.div
                                            className="bg-white rounded-xl px-6 py-5 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center h-28 md:h-32 w-full sm:w-72 md:w-80"
                                            role="listitem"
                                            whileHover={{ scale: 1.05, y: -5 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            <img
                                                src={require('../images/partners/mitre-10.webp')}
                                                alt="Mitre 10 - Trusted Partner"
                                                className="max-h-14 md:max-h-16 w-auto object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                                            />
                                        </motion.div>
                                    </div>
                                </div>

                                {/* Row 3 */}
                                <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6">
                                    <motion.div
                                        className="bg-white rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center h-20"
                                        role="listitem"
                                        whileHover={{ scale: 1.05, y: -5 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <img
                                            src={require('../images/partners/mammoth.png')}
                                            alt="Mammoth - Trusted Partner"
                                            className="max-h-10 w-auto object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                                        />
                                    </motion.div>
                                    <motion.div
                                        className="bg-white rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center h-20"
                                        role="listitem"
                                        whileHover={{ scale: 1.05, y: -5 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <img
                                            src={require('../images/partners/knaufinsulation.png')}
                                            alt="Knauf Insulation - Trusted Partner"
                                            className="max-h-10 w-auto object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                                        />
                                    </motion.div>
                                    <motion.div
                                        className="bg-white rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center h-20"
                                        role="listitem"
                                        whileHover={{ scale: 1.05, y: -5 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <img
                                            src={require('../images/partners/greenstuf.png')}
                                            alt="GreenStuf - Trusted Partner"
                                            className="max-h-10 w-auto object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                                        />
                                    </motion.div>
                                    <motion.div
                                        className="bg-white rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center h-20"
                                        role="listitem"
                                        whileHover={{ scale: 1.05, y: -5 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <img
                                            src={require('../images/partners/expol.jpeg')}
                                            alt="Expol - Trusted Partner"
                                            className="max-h-10 w-auto object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                                        />
                                    </motion.div>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Bottom CTA */}
                    <motion.div
                        className="text-center mt-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                    >
                        <p className="text-white/90 mb-6">
                            Ready to work with trusted professionals?
                        </p>
                        <motion.button
                            className="bg-brand-orange text-white px-8 py-3 rounded-lg font-semibold hover:bg-brand-orange-hover transition-colors duration-300"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handleGetQuote}
                        >
                            Get Your Free Quote
                        </motion.button>
                    </motion.div>
                </motion.section>

                {/* Client Testimonial Section */}
                <motion.section
                    className="relative py-24 md:py-60 bg-brand-green"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.8 }}
                    aria-labelledby="testimonial-heading"
                >
                    {/* Slanted Background Block */}
                    <div
                        className="absolute inset-0 bg-brand-cream"
                        style={{
                            clipPath: 'polygon(0 15%, 100% 0, 100% 85%, 0 100%)'
                        }}
                        aria-hidden="true"
                    ></div>

                    {/* Content */}
                    <div className="relative max-w-7xl mx-auto px-4">
                        <div className="flex items-center">
                            {/* Left-aligned Testimonial Content */}
                            <motion.div
                                className="text-black space-y-6 text-left max-w-4xl"
                                initial={{ opacity: 0, y: 100 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 1, ease: "easeOut" }}
                            >
                                {/* Profile Picture */}
                                <motion.div
                                    className="flex items-center mb-6"
                                    initial={{ opacity: 0, y: 50 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                                >
                                    <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-300 mr-4">
                                        <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-400 flex items-center justify-center">
                                            <span className="text-gray-800 font-semibold text-lg">JL</span>
                                        </div>
                                    </div>
                                </motion.div>

                                {/* Testimonial Text */}
                                <motion.blockquote
                                    className="text-xl md:text-2xl lg:text-3xl leading-loose font-serif italic max-w-4xl"
                                    initial={{ opacity: 0, y: 0 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
                                    cite="Jessica Lee, Auckland"
                                >
                                    "Mega Property Solutions transformed our home in Auckland with excellent insulation services. Their team was professional, punctual, and incredibly efficient. We immediately noticed a difference in warmth and energy savings. Highly recommend their services to anyone in need!"
                                </motion.blockquote>

                                {/* Client Name */}
                                <motion.div
                                    className="text-lg font-semibold font-mono"
                                    initial={{ opacity: 0, y: 50 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
                                >
                                    - Jessica Lee
                                </motion.div>
                            </motion.div>
                        </div>
                    </div>
                </motion.section>

                {/* FAQ Section */}
                <motion.section
                    className="bg-brand-green py-24 md:py-32"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.8 }}
                    aria-labelledby="faq-heading"
                >
                    <div className="max-w-6xl mx-auto px-4">
                        {/* Heading */}
                        <motion.h2
                            id="faq-heading"
                            className="text-3xl md:text-4xl font-bold text-white mb-12 text-left"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.6 }}
                        >
                            Need Help? Find Answers Here
                        </motion.h2>

                        {/* FAQ Items */}
                        <motion.div
                            className="space-y-0"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            role="list"
                            aria-label="Frequently asked questions"
                        >
                            {/* FAQ Item 1 */}
                            <div className="border-b border-white/20 py-6" role="listitem">
                                <div
                                    className="flex justify-between items-center cursor-pointer"
                                    onClick={() => toggleFAQ(0)}
                                    onKeyDown={(e) => e.key === 'Enter' && toggleFAQ(0)}
                                    tabIndex={0}
                                    role="button"
                                    aria-expanded={openFAQ === 0}
                                    aria-controls="faq-answer-0"
                                >
                                    <h3 className="text-lg font-medium text-white">
                                        What insulation services do you offer?
                                    </h3>
                                    <svg
                                        className={`w-5 h-5 text-white/70 transition-transform duration-200 ${openFAQ === 0 ? 'rotate-180' : 'rotate-0'}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                                {openFAQ === 0 && (
                                    <div id="faq-answer-0" className="mt-4 text-white/90 leading-relaxed" role="region" aria-labelledby="faq-question-0">
                                        <p>We offer comprehensive insulation services including ceiling insulation, wall insulation, underfloor insulation, and roof space insulation. Our services cover both residential and commercial properties, with options for retrofitting existing buildings and installing insulation in new constructions. We also provide thermal imaging assessments to identify heat loss areas and recommend the most effective insulation solutions for your specific needs.</p>
                                    </div>
                                )}
                            </div>

                            {/* FAQ Item 2 */}
                            <div className="border-b border-white/20 py-6" role="listitem">
                                <div
                                    className="flex justify-between items-center cursor-pointer"
                                    onClick={() => toggleFAQ(1)}
                                    onKeyDown={(e) => e.key === 'Enter' && toggleFAQ(1)}
                                    tabIndex={0}
                                    role="button"
                                    aria-expanded={openFAQ === 1}
                                    aria-controls="faq-answer-1"
                                >
                                    <h3 className="text-lg font-medium text-white">
                                        Do you offer free insulation assessments?
                                    </h3>
                                    <svg
                                        className={`w-5 h-5 text-gray-600 transition-transform duration-200 ${openFAQ === 1 ? 'rotate-180' : 'rotate-0'}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                                {openFAQ === 1 && (
                                    <div id="faq-answer-1" className="mt-4 text-white/90 leading-relaxed" role="region" aria-labelledby="faq-question-1">
                                        <p>Yes, we provide complimentary insulation assessments for all Auckland properties. Our qualified technicians will visit your home to evaluate your current insulation, identify areas of heat loss, and provide a detailed report with recommendations. The assessment includes thermal imaging scans, moisture testing, and a comprehensive evaluation of your property's insulation needs. There's no obligation to proceed with our services after the assessment.</p>
                                    </div>
                                )}
                            </div>

                            {/* FAQ Item 3 */}
                            <div className="border-b border-white/20 py-6" role="listitem">
                                <div
                                    className="flex justify-between items-center cursor-pointer"
                                    onClick={() => toggleFAQ(2)}
                                    onKeyDown={(e) => e.key === 'Enter' && toggleFAQ(2)}
                                    tabIndex={0}
                                    role="button"
                                    aria-expanded={openFAQ === 2}
                                    aria-controls="faq-answer-2"
                                >
                                    <h3 className="text-lg font-medium text-white">
                                        Are your services available throughout Auckland?
                                    </h3>
                                    <svg
                                        className={`w-5 h-5 text-gray-600 transition-transform duration-200 ${openFAQ === 2 ? 'rotate-180' : 'rotate-0'}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                                {openFAQ === 2 && (
                                    <div id="faq-answer-2" className="mt-4 text-white/90 leading-relaxed" role="region" aria-labelledby="faq-question-2">
                                        <p>Absolutely! We service the entire Auckland region including North Shore, Waitakere, Manukau, and the Auckland City area. Our team covers all suburbs from Albany in the north to Pukekohe in the south, and from the Waitakere Ranges in the west to Howick in the east. We also extend our services to the surrounding areas including Waiheke Island and the Hibiscus Coast. Contact us to confirm coverage for your specific location.</p>
                                    </div>
                                )}
                            </div>

                            {/* FAQ Item 4 */}
                            <div className="border-b border-white/20 py-6" role="listitem">
                                <div
                                    className="flex justify-between items-center cursor-pointer"
                                    onClick={() => toggleFAQ(3)}
                                    onKeyDown={(e) => e.key === 'Enter' && toggleFAQ(3)}
                                    tabIndex={0}
                                    role="button"
                                    aria-expanded={openFAQ === 3}
                                    aria-controls="faq-answer-3"
                                >
                                    <h3 className="text-lg font-medium text-white">
                                        What types of insulation materials do you use?
                                    </h3>
                                    <svg
                                        className={`w-5 h-5 text-gray-600 transition-transform duration-200 ${openFAQ === 3 ? 'rotate-180' : 'rotate-0'}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                                {openFAQ === 3 && (
                                    <div id="faq-answer-3" className="mt-4 text-white/90 leading-relaxed" role="region" aria-labelledby="faq-question-3">
                                        <p>We use only high-quality, NZ Building Code compliant insulation materials including Pink Batts, Earthwool, and other premium brands. Our materials include glass wool, polyester, and natural wool options, all with excellent thermal performance ratings. We also offer fire-resistant and acoustic insulation solutions. All our materials are environmentally friendly, non-toxic, and come with manufacturer warranties. We can recommend the best material for your specific climate zone and building requirements.</p>
                                    </div>
                                )}
                            </div>

                            {/* FAQ Item 5 */}
                            <div className="border-b border-white/20 py-6" role="listitem">
                                <div
                                    className="flex justify-between items-center cursor-pointer"
                                    onClick={() => toggleFAQ(4)}
                                    onKeyDown={(e) => e.key === 'Enter' && toggleFAQ(4)}
                                    tabIndex={0}
                                    role="button"
                                    aria-expanded={openFAQ === 4}
                                    aria-controls="faq-answer-4"
                                >
                                    <h3 className="text-lg font-medium text-white">
                                        How can I book a service with Mega Property Solutions?
                                    </h3>
                                    <svg
                                        className={`w-5 h-5 text-gray-600 transition-transform duration-200 ${openFAQ === 4 ? 'rotate-180' : 'rotate-0'}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                                {openFAQ === 4 && (
                                    <div id="faq-answer-4" className="mt-4 text-white/90 leading-relaxed" role="region" aria-labelledby="faq-question-4">
                                        <p>Booking our services is easy! You can call us directly on 021-520-201 / 021-229-8869, email us at operation@meganz.co.nz, or fill out the contact form on our website. We'll schedule a free assessment at your convenience, typically within 1-2 business days. After the assessment, we'll provide you with a detailed quote and can schedule the installation work. We offer flexible scheduling including weekend appointments, and our team will arrive on time and complete the work efficiently with minimal disruption to your daily routine.</p>
                                    </div>
                                )}
                            </div>

                            {/* FAQ Item 6 */}
                            <div className="border-b border-white/20 py-6" role="listitem">
                                <div
                                    className="flex justify-between items-center cursor-pointer"
                                    onClick={() => toggleFAQ(5)}
                                    onKeyDown={(e) => e.key === 'Enter' && toggleFAQ(5)}
                                    tabIndex={0}
                                    role="button"
                                    aria-expanded={openFAQ === 5}
                                    aria-controls="faq-answer-5"
                                >
                                    <h3 className="text-lg font-medium text-white">
                                        Do you provide any guarantees for your work?
                                    </h3>
                                    <svg
                                        className={`w-5 h-5 text-gray-600 transition-transform duration-200 ${openFAQ === 5 ? 'rotate-180' : 'rotate-0'}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                                {openFAQ === 5 && (
                                    <div id="faq-answer-5" className="mt-4 text-white/90 leading-relaxed" role="region" aria-labelledby="faq-question-5">
                                        <p>Yes, we stand behind our work with comprehensive guarantees. All our installations come with a 2-year workmanship guarantee, and the insulation materials themselves carry manufacturer warranties of up to 50 years. We also provide a 100% satisfaction guarantee - if you're not completely satisfied with our work, we'll return to fix any issues at no additional cost. Our team is fully licensed and insured, giving you complete peace of mind that your investment is protected.</p>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </div>
                </motion.section>

                {/* Call-to-Action Section */}
                <motion.section
                    className="py-24 md:py-32 bg-brand-cream"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.8 }}
                    aria-labelledby="cta-heading"
                >
                    <div className="max-w-4xl mx-auto px-4 text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.8 }}
                        >
                            <h2 id="cta-heading" className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
                                Ready to Transform Your Home?
                            </h2>
                            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                                Whether you're building from the ground up or upgrading an existing property, we're here to make your space warmer, safer, and more energy-efficient.
                            </p>

                            <motion.div
                                className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                            >
                                <motion.button
                                    className="bg-brand-orange text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-brand-orange-hover transition-colors duration-300 shadow-lg hover:shadow-xl"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => handleServiceInquiry('Free Assessment')}
                                >
                                    Get Free Assessment
                                </motion.button>
                            </motion.div>

                            <motion.p
                                className="text-sm text-gray-500 mt-6"
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.8, delay: 0.4 }}
                            >
                                No obligation • Free consultation • Licensed & insured
                            </motion.p>
                        </motion.div>
                    </div>
                </motion.section>
            </main>
        </>
    );
}

export default Home;
